// Width-measured file-path truncation (KIR-2563).
//
// The visible cell width — not a character budget — decides how much of a path
// is shown. Callers inject a `measure` function that returns the rendered pixel
// width of a candidate string (see `measureTextWidth`), which is what lets this
// respond to responsive layout, table resizing, font rendering and zoom.
//
// The path-shape helpers (separator normalization, ambiguous-basename tail)
// were lifted from the char-budget `TruncatedPath` this module replaced.

// Character inserted where path segments or filename characters are elided.
export const PATH_ELLIPSIS = '…';

// Filenames that are ambiguous on their own — every skill's file is literally
// "SKILL.md", every memory is "CLAUDE.md", every agent list is "AGENTS.md".
// Keep the containing folder visible so rows stay distinguishable.
const FOLDER_DISAMBIGUATED_BASENAMES = new Set([
  'claude.md',
  'agents.md',
  'skill.md',
]);

// KIR-2548: forward-slash so segment-split truncation doesn't treat a Windows
// backslash path as one un-splittable token. Display-only; backend data is
// untouched.
export function normalizePathSeparators(path: string): string {
  return path.replace(/\\/g, '/');
}

// The "tail" is the part of the path kept intact on the right: normally the
// filename, but the parent folder too for ambiguous basenames.
export function getDisambiguatedTail(segments: string[]): string {
  const last = segments[segments.length - 1] ?? '';
  if (
    FOLDER_DISAMBIGUATED_BASENAMES.has(last.toLowerCase()) &&
    segments.length >= 2
  ) {
    return segments.slice(-2).join('/');
  }
  return last;
}

/**
 * Pick the widest representation of `rawPath` that fits within
 * `availableWidthPx`, following the KIR-2563 fallback ladder:
 *
 *   1. Full path.
 *   2. Middle truncation keeping the most leading segments + full tail:
 *      `root/…/CLAUDE.md`.
 *   3. Filename (+ extension) only: `CLAUDE.md`.
 *   4. Middle-ellipsis the filename, preserving the extension:
 *      `some-long-fi….space.md`.
 *
 * `measure` must return the rendered pixel width of the given text. The
 * function is pure and DOM-free so it can be unit-tested with a fake measurer.
 */
export function truncatePathToWidth(
  rawPath: string,
  availableWidthPx: number,
  measure: (text: string) => number
): string {
  const path = normalizePathSeparators(rawPath);

  // No usable width yet (initial render before ResizeObserver fires): show the
  // full path; the caller re-runs once a real width is known.
  if (!Number.isFinite(availableWidthPx) || availableWidthPx <= 0) {
    return path;
  }

  // Step 1: full path fits.
  if (measure(path) <= availableWidthPx) {
    return path;
  }

  const segments = path.split('/');
  // Drop trailing empty segments so a directory-style path ("…/dir/") truncates
  // to its last real segment instead of an empty string (which would render a
  // blank cell). Step 1 already returned the full path verbatim when it fit, so
  // trimming here only affects the truncated candidates.
  while (segments.length > 1 && segments[segments.length - 1] === '') {
    segments.pop();
  }
  const tail = getDisambiguatedTail(segments);
  const tailSegmentCount = tail.includes('/') ? 2 : 1;
  const leadingSegments = segments.slice(0, segments.length - tailSegmentCount);

  // Step 2: middle truncation — keep as many leading segments as fit while
  // always keeping the full tail. Try the most leading segments first and
  // shrink; the widest fitting candidate wins.
  if (leadingSegments.length > 0) {
    for (let keep = leadingSegments.length; keep >= 1; keep--) {
      const prefix = leadingSegments.slice(0, keep).join('/');
      const candidate = `${prefix}/${PATH_ELLIPSIS}/${tail}`;
      if (measure(candidate) <= availableWidthPx) {
        return candidate;
      }
    }
    // Not even one leading segment + tail fits: try a bare `…/tail`.
    const ellipsisTail = `${PATH_ELLIPSIS}/${tail}`;
    if (measure(ellipsisTail) <= availableWidthPx) {
      return ellipsisTail;
    }
  }

  // Step 3: ellipsis-free fallbacks, widest first.
  const filename = segments[segments.length - 1] ?? path;

  // 3a: the disambiguated tail without a leading ellipsis (e.g.
  // "skills/CLAUDE.md"). Reached when even "…/tail" didn't fit; keep the parent
  // folder while it still fits so ambiguous basenames stay distinguishable,
  // rather than dropping straight to the bare filename. Consistent with 3b,
  // which is also ellipsis-free.
  if (tail !== filename && measure(tail) <= availableWidthPx) {
    return tail;
  }

  // 3b: filename (+ extension) only.
  if (measure(filename) <= availableWidthPx) {
    return filename;
  }

  // Step 4: middle-ellipsis the filename, keeping the extension.
  return truncateFilenameToWidth(filename, availableWidthPx, measure);
}

// Elide the middle of a filename while preserving its extension. Returns the
// widest fitting `head…tail.ext`, or a bare `….ext` when nothing else fits (the
// container's own overflow clipping is the last line of defence).
function truncateFilenameToWidth(
  filename: string,
  availableWidthPx: number,
  measure: (text: string) => number
): string {
  const dotIndex = filename.lastIndexOf('.');
  // `dotIndex > 0` keeps dotfiles like ".bashrc" whole (no real extension).
  const hasExtension = dotIndex > 0;
  const extension = hasExtension ? filename.slice(dotIndex) : '';
  const base = hasExtension ? filename.slice(0, dotIndex) : filename;

  const minimal = `${PATH_ELLIPSIS}${extension}`;
  if (measure(minimal) > availableWidthPx) {
    return minimal;
  }

  // Keep `keep` characters of the base split head-biased around the ellipsis;
  // the first (largest) that fits wins.
  for (let keep = base.length - 1; keep >= 1; keep--) {
    const headLength = Math.ceil(keep / 2);
    const tailLength = Math.floor(keep / 2);
    const head = base.slice(0, headLength);
    const tail = tailLength > 0 ? base.slice(base.length - tailLength) : '';
    const candidate = `${head}${PATH_ELLIPSIS}${tail}${extension}`;
    if (measure(candidate) <= availableWidthPx) {
      return candidate;
    }
  }

  return minimal;
}
